/* eslint-disable max-lines */
import EventBus from "common/eventbus";
import Events from "common/events";
import { api } from "jiffy-api";
import { track, trackRudderstack } from "jiffy-analytics";
import { redirectTo } from "common/utils/browser";
import { startMixpanelRecording } from "common/utils/mixpanel/tracker";
import { waitForWindowVariable, waitFor } from "common/utils/ui/wait_for";
import shouldShowSampleLimitModal from "common/utils/ui/sample-limit-checker";
import { validateArtworkFileType } from "common/artwork-file-types";
import { submitDtfEvent, submitCompletedEvent } from "common/utils/mixpanel/submit-dtf-events";
import { trackRudderstackImageUploadStarted } from "common/utils/rudderstack/tracker";
import buildDesignLabUrl from "common/utils/design-lab-url";
import { getActiveColorName, collectPdpData } from "common/jiffy/pdp-data-collection";
import { showSampleLimitModal } from "../product-details/product-details-sample-order-limit-modal/product-details-sample-order-limit-modal";
import DesignUploadCtaPlpCookieClass from "./design-upload-cta-plp/design-upload-cta-plp-cookie-class";
import "./design-upload-cta.scss";

const instances = new Map();

class DesignUploadCTA {
  constructor(container) {
    this.container = container;
    this.initData();
    this.initSelectors();
    this.bindEventListeners();
  }

  /**
   * Initialize DOM element selectors
   * @private
   */
  initSelectors() {
    this.$dropZone = this.container.querySelector(".js-dropzone");
    this.$myDeviceBtn = this.container.querySelector(".js-my-device-btn");
    this.$myLibraryBtn = this.container.querySelector(".js-my-library-btn");
    this.$weaverAiStudioBtn = this.container.querySelector(".js-weaver-ai-studio-btn");
    this.$canvaBtn = this.container.querySelector(".js-canva-btn");
    this.$fileInput = this.container.querySelector(".js-design-file-input");
    this.$continueDesigningCta = this.container.querySelector(".js-continue-designing-cta");
    this.$continueDesigningLoader = this.container.querySelector(".js-continue-designing-loader");
    this.$continueDesigningBtn = this.container.querySelector(".js-continue-designing-cta-btn");
    this.$continueDesigningFront = this.container.querySelector(".js-continue-designing-cta-front");
    this.$continueDesigningBack = this.container.querySelector(".js-continue-designing-cta-back");
  }

  /**
   * Initialize data
   * @private
   */
  initData() {
    this.catalogNumber = this.container.dataset.catalogNumber;
    this.businessLine = this.container.dataset.businessLine;
    this.color = this.container.dataset.color;
    this.isSample = this.container.dataset.sample === "true";
    this.side = this.container.dataset.side || "front";
    this.source = this.container.dataset.source;
    this.project_id = "";
    this.location = this.container.dataset.pageName;
    this.compositionId = this.container.dataset.compositionId;
    // Sample mode is not handled by this component
    this.isModal = this.container.dataset.isModal === "true";
    this.designProject = null;
    this.plpUploadedDesigns = undefined;
    // Use Map to store uploadUuid per file to prevent race conditions
    this.uploadUuids = new Map();
  }

  /**
   * Bind event listeners
   * @private
   */
  bindEventListeners() {
    this.handleDragEnter = this.handleDragEnter.bind(this);
    this.handleDragLeave = this.handleDragLeave.bind(this);
    this.handleDrop = this.handleDrop.bind(this);
    this.handleFileSelect = this.handleFileSelect.bind(this);
    this.handleMyDeviceClick = this.withSampleLimitCheck(this.handleMyDeviceClick.bind(this));
    this.handleMyLibraryClick = this.withSampleLimitCheck(this.handleMyLibraryClick.bind(this));
    this.handleCanvaClick = this.withSampleLimitCheck(this.handleCanvaClick.bind(this));
    this.handleWeaverAiStudioClick = this.withSampleLimitCheck(
      this.handleWeaverAiStudioClick.bind(this)
    );
    this.handleContinueDesigningClick = this.handleContinueDesigningClick.bind(this);
  }

  get #isDesignProjectExists() {
    return this.designProject?.placements && Object.keys(this.designProject.placements).length > 0;
  }

  get #isUploadedToDesignLab() {
    return this.#isDesignProjectExists;
  }

  get #isPLPDesignPresent() {
    return this.plpUploadedDesigns?.isPLPUploadedDesignPresent;
  }

  get #shouldUsePLPDesign() {
    if (!this.#isPLPDesignPresent) {
      return false;
    }

    if (!this.#isDesignProjectExists && this.#isPLPDesignPresent) {
      return true;
    }

    if (this.plpUploadedDesigns.updatedAt) {
      const plpDate = new Date(this.plpUploadedDesigns.updatedAt);
      const projectDate = new Date(this.designProject.updated_at);

      if (Number.isNaN(plpDate.getTime()) || Number.isNaN(projectDate.getTime())) {
        // eslint-disable-next-line no-console
        console.error("Invalid date detected in design comparison");
        return false;
      }

      return plpDate > projectDate;
    }

    return false;
  }

  get #imageUrls() {
    let images = { front: null, back: null };

    const hasDesignProject = this.#isDesignProjectExists;
    const hasPLPDesign = this.#isPLPDesignPresent;

    if (hasDesignProject && !hasPLPDesign) {
      return this.extractProjectImages();
    }
    if (!hasDesignProject && hasPLPDesign) {
      images.front = this.plpUploadedDesigns.imageUrl;
      return images;
    }

    if (this.#shouldUsePLPDesign) {
      images.front = this.plpUploadedDesigns.imageUrl;
    } else if (hasDesignProject) {
      images = this.extractProjectImages();
    }

    return images;
  }

  init() {
    this.attachEventListeners();
    this.loadPLPUploadedDesigns();
    this.loadDesignProject();

    waitForWindowVariable("mixpanel", () => {
      startMixpanelRecording(100);
    });

    EventBus.on(Events.PRODUCT_COLOR_SELECTED, async (evt, { color, compositionId }) => {
      this.handleDataUpdate({
        catalogNumber: this.catalogNumber,
        color,
        compositionId,
        sample: this.isSample ? "true" : "false"
      });
    });
  }

  attachEventListeners() {
    this.attachDropZoneEventListeners();
    this.attachButtonEventListeners();
  }

  static preventDefaults(e) {
    e.preventDefault();
    e.stopPropagation();
  }

  handleDataUpdate({ catalogNumber, color, compositionId, sample }) {
    if (!catalogNumber || !color) return;

    this.catalogNumber = catalogNumber;
    this.color = color;
    this.compositionId = compositionId;
    if (sample !== undefined) {
      this.isSample = sample === "true";
    }

    if (this.$myLibraryBtn) {
      const currentUrl = new URL(this.$myLibraryBtn.href);
      currentUrl.searchParams.set("ac", color);
      if (this.isSample) {
        currentUrl.searchParams.set("sample", "true");
      }
      this.$myLibraryBtn.href = currentUrl.toString();
    }

    if (this.$canvaBtn) {
      const currentUrl = new URL(this.$canvaBtn.href);
      currentUrl.searchParams.set("ac", color);
      if (this.isSample) {
        currentUrl.searchParams.set("sample", "true");
      }
      this.$canvaBtn.href = currentUrl.toString();
    }

    if (this.$weaverAiStudioBtn) {
      const currentUrl = new URL(this.$weaverAiStudioBtn.href);
      currentUrl.searchParams.set("ac", color);
      currentUrl.searchParams.set("product", catalogNumber);
      if (this.isSample) {
        currentUrl.searchParams.set("sample", "true");
      }
      this.$weaverAiStudioBtn.href = currentUrl.toString();
    }
  }

  loadPLPUploadedDesigns() {
    const imageUrl = DesignUploadCtaPlpCookieClass.originalUrl;
    const { previewUrl } = DesignUploadCtaPlpCookieClass;
    const imageId = DesignUploadCtaPlpCookieClass.previewId;
    const imageType = DesignUploadCtaPlpCookieClass.originalFiletype;
    const { updatedAt } = DesignUploadCtaPlpCookieClass;
    const isPLPUploadedDesignPresent = Boolean(imageUrl && imageId && imageType);

    this.plpUploadedDesigns = {
      isPLPUploadedDesignPresent,
      imageUrl,
      imageId,
      imageType,
      updatedAt,
      previewUrl
    };
  }

  async loadDesignProject() {
    if (!this.$continueDesigningCta) return;

    try {
      this.designProject = await api.designLab.getLatestDesignProject();

      this.processContinueDesign();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error loading design project:", error);
      this.hideContinueDesigning();
    }
  }

  processContinueDesign() {
    if (!this.designProject && !this.#isPLPDesignPresent) {
      this.hideContinueDesigning();
      return;
    }

    const images = this.#imageUrls;

    if (this.plpUploadedDesigns.previewUrl) {
      this.updateContinueDesigningUI({ front: this.plpUploadedDesigns.previewUrl });
    } else if (images.front || images.back) {
      this.updateContinueDesigningUI(images);
    } else {
      this.hideContinueDesigning();
    }
  }

  extractProjectImages() {
    const images = { front: null, back: null };
    const { placements } = this.designProject;

    if (!placements || Object.keys(placements).length === 0) {
      return images;
    }

    if (placements.front && placements.front.dtf_artwork) {
      const frontPlacement = placements.front;
      const frontArtwork = frontPlacement.dtf_artwork;

      images.front = frontPlacement.remove_background
        ? frontArtwork.no_bg_url || frontArtwork.no_bg_no_trim_url
        : frontArtwork.with_bg_url || frontArtwork.original_image_url;
    }

    if (placements.back && placements.back.dtf_artwork) {
      const backPlacement = placements.back;
      const backArtwork = backPlacement.dtf_artwork;

      images.back = backPlacement.remove_background
        ? backArtwork.no_bg_url || backArtwork.no_bg_no_trim_url
        : backArtwork.with_bg_url || backArtwork.original_image_url;
    }

    return images;
  }

  updateContinueDesigningUI(images) {
    this.updateImageContainer(this.$continueDesigningFront, images.front);
    this.updateImageContainer(this.$continueDesigningBack, images.back);
    this.showContinueDesigning();
  }

  // eslint-disable-next-line class-methods-use-this
  updateImageContainer(container, imageUrl) {
    if (!container) return;

    const img = container.querySelector("img");
    if (imageUrl && img) {
      img.src = imageUrl;
      container.classList.remove("hidden");
    } else {
      container.classList.add("hidden");
    }
  }

  showContinueDesigning() {
    if (this.$continueDesigningLoader) {
      this.$continueDesigningLoader.classList.add("hidden");
    }
  }

  hideContinueDesigning() {
    if (this.$continueDesigningCta) {
      this.$continueDesigningCta.classList.add("hidden");
    }

    if (this.isModal) {
      const $modal = document.getElementById("design-upload-modal");
      $modal?.classList?.add("design-upload-modal--no-project");
    }
  }

  getPdpData() {
    // Use stored PDP data from modal if available, otherwise collect it
    let quantities = this.pdpQuantities;
    let sizeData = this.pdpSizeData;

    if (!quantities || !sizeData) {
      // Always get the current color from URL to handle dynamic color changes
      const activeColor = getActiveColorName() || this.color;
      if (activeColor) {
        const pdpData = collectPdpData(activeColor);
        quantities = pdpData.quantities;
        sizeData = pdpData.sizeData;
      }
    }

    return { quantities, sizeData };
  }

  #addPdpParamsToUrl(url, pdpData, currentColor) {
    if (this.catalogNumber) {
      url.searchParams.set("catalog_number", this.catalogNumber);
    }
    if (currentColor) {
      url.searchParams.set("ac", currentColor);
    }
    if (pdpData.quantities) {
      url.searchParams.set("pdp_quantities", JSON.stringify(pdpData.quantities));
    }
    if (pdpData.sizeData) {
      url.searchParams.set("pdp_size_data", JSON.stringify(pdpData.sizeData));
    }
  }

  buildContinueDesigningUrl(pdpData) {
    // Always get current color from URL to handle dynamic color changes
    const currentColor = getActiveColorName() || this.color;

    if (this.#shouldUsePLPDesign) {
      return buildDesignLabUrl({
        ...this.plpUploadedDesigns,
        productData: { catalogNumber: this.catalogNumber, color: currentColor },
        pdpQuantities: pdpData.quantities,
        pdpSizeData: pdpData.sizeData
      });
    }

    // If we have a design project with UUID, navigate directly to it to avoid showing the modal
    if (this.designProject?.uuid) {
      const url = new URL(`/design-lab/${this.designProject.uuid}`, window.location.origin);
      this.#addPdpParamsToUrl(url, pdpData, currentColor);
      return url.toString();
    }

    const url = new URL(this.$continueDesigningBtn.href, window.location.origin);
    this.#addPdpParamsToUrl(url, pdpData, currentColor);
    if (
      this.plpUploadedDesigns.imageId &&
      this.plpUploadedDesigns.imageUrl &&
      this.plpUploadedDesigns.imageType
    ) {
      url.searchParams.set(
        "artwork_urls",
        JSON.stringify({ [this.plpUploadedDesigns.imageId]: this.plpUploadedDesigns.imageUrl })
      );
      url.searchParams.set("file_content_type", this.plpUploadedDesigns.imageType);
    }
    return url.toString();
  }

  async handleContinueDesigningClick(e) {
    e.preventDefault();

    // Disable the button to prevent multiple clicks
    this.$continueDesigningBtn.classList.add("opacity-50", "pointer-events-none");

    // Track the click event
    const currentColor = getActiveColorName() || this.color;
    track("continue_designing_clicked", {
      catalog_number: this.catalogNumber,
      composition_name: currentColor,
      design_project_id: this.designProject?.id,
      source: this.#isPLPDesignPresent ? "plp_uploaded_design" : "upload_cta"
    });

    // If not in modal, update the design project with current product first
    if (this.designProject?.id) {
      try {
        await this.updateDesignProjectComposition();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error updating design project:", error);
        // Continue with navigation even if update fails
      }
    }

    const pdpData = this.getPdpData();
    const redirectUrl = this.buildContinueDesigningUrl(pdpData);

    DesignUploadCtaPlpCookieClass.destroy();

    redirectTo(redirectUrl);
  }

  async updateDesignProjectComposition() {
    if (!this.compositionId) return;

    // Flatten quantities from { catalogNumber: { compositionId: {...} } } to { compositionId: {...} }
    const flattenedQuantities = {};
    Object.values(this.designProject.quantities || {}).forEach(compositions => {
      Object.assign(flattenedQuantities, compositions);
    });

    // Add the new composition ID if it's not already there
    if (!flattenedQuantities[this.compositionId]) {
      flattenedQuantities[this.compositionId] = {};
    }

    await api.designLab.updateDesignProject(
      { id: this.designProject.id },
      {
        made_design_project: {
          active_composition_id: this.compositionId,
          quantities: flattenedQuantities
        }
      }
    );
  }

  handleDragEnter(e) {
    DesignUploadCTA.preventDefaults(e);
    this.$dropZone.classList.add("border-blue-600", "bg-blue-50");
  }

  handleDragLeave(e) {
    DesignUploadCTA.preventDefaults(e);
    if (e.target === this.$dropZone) {
      this.$dropZone.classList.remove("border-blue-600", "bg-blue-50");
    }
  }

  handleDrop(e) {
    DesignUploadCTA.preventDefaults(e);
    this.$dropZone.classList.remove("border-blue-600", "bg-blue-50");

    const { files } = e.dataTransfer;
    if (files && files.length > 0) {
      this.processFile(files[0]);
    }
  }

  handleMyDeviceClick(e) {
    e.preventDefault();
    e.stopPropagation();

    this.triggerSource = this.$myDeviceBtn.dataset.trigger || "upload_section";

    if (this.$myDeviceBtn.dataset.trigger) {
      this.triggerText = this.$myDeviceBtn.dataset.triggerText;

      this.$myDeviceBtn.removeAttribute("data-trigger");
      this.$myDeviceBtn.removeAttribute("data-trigger-text");
    }

    this.$fileInput.click();
  }

  handleMyLibraryClick(e) {
    e.preventDefault();

    const targetUrl = new URL(this.$myLibraryBtn.href);
    this.source = "design_library";

    const { quantities, sizeData } = this.getPdpData();

    if (quantities) {
      targetUrl.searchParams.set("pdp_quantities", JSON.stringify(quantities));
    }
    if (sizeData) {
      targetUrl.searchParams.set("pdp_size_data", JSON.stringify(sizeData));
    }

    const currentColor = getActiveColorName() || this.color;

    track("start_designing_clicked", {
      catalog_number: this.catalogNumber,
      composition_name: currentColor,
      side: this.side,
      source: this.source,
      trigger: "upload_section"
    });

    trackRudderstack(
      "Pilot: CTA Clicked",
      {
        cta_type: "start_designing_clicked",
        type: "button",
        text: "My Design Library",
        destination: "Design Lab",
        catalog_number: this.catalogNumber,
        composition_name: currentColor,
        source: this.source,
        trigger: "upload_section"
      },
      true
    );

    redirectTo(targetUrl.toString());
  }

  handleCanvaClick(e) {
    e.preventDefault();

    const targetUrl = new URL(this.$canvaBtn.href);
    this.source = "canva";

    const { quantities, sizeData } = this.getPdpData();

    if (quantities) {
      targetUrl.searchParams.set("pdp_quantities", JSON.stringify(quantities));
    }
    if (sizeData) {
      targetUrl.searchParams.set("pdp_size_data", JSON.stringify(sizeData));
    }

    const currentColor = getActiveColorName() || this.color;

    track("start_designing_clicked", {
      catalog_number: this.catalogNumber,
      composition_name: currentColor,
      side: this.side,
      source: this.source,
      trigger: "upload_section"
    });

    trackRudderstack(
      "Pilot: CTA Clicked",
      {
        cta_type: "start_designing_clicked",
        type: "button",
        text: "Canva",
        destination: "Design Lab",
        catalog_number: this.catalogNumber,
        composition_name: currentColor,
        source: this.source,
        trigger: "upload_section"
      },
      true
    );

    redirectTo(targetUrl.toString());
  }

  handleWeaverAiStudioClick(e) {
    e.preventDefault();

    const targetUrl = new URL(this.$weaverAiStudioBtn.href);
    const { quantities, sizeData } = this.getPdpData();

    if (quantities) {
      targetUrl.searchParams.set("pdp_quantities", JSON.stringify(quantities));
    }
    if (sizeData) {
      targetUrl.searchParams.set("pdp_size_data", JSON.stringify(sizeData));
    }

    const currentColor = getActiveColorName() || this.color;
    track("ai_studio_cta_clicked", {
      catalog_number: this.catalogNumber,
      composition_name: currentColor,
      source: "design_upload_cta"
    });

    redirectTo(targetUrl.toString());
  }

  handleFileSelect(e) {
    const { files } = e.target;
    if (files && files.length > 0) {
      this.processFile(files[0]);
    }
    // Reset input
    e.target.value = "";
  }

  // File type validation using shared artwork file types module
  static validateFileType(file) {
    return validateArtworkFileType(file);
  }

  processFile(file) {
    // Validate file type before processing
    const { valid, message } = DesignUploadCTA.validateFileType(file);

    if (!valid) {
      this.showError(message);
      return;
    }

    // Only set source to "upload" if it hasn't been set already (e.g., by camera upload)
    if (!this.source) {
      this.source = "upload";
    }

    const currentColor = getActiveColorName() || this.color;

    track("start_designing_clicked", {
      catalog_number: this.catalogNumber,
      composition_name: currentColor,
      side: this.side,
      source: this.source,
      trigger: this.triggerSource || "upload_section"
    });

    trackRudderstack(
      "Pilot: CTA Clicked",
      {
        cta_type: "start_designing_clicked",
        type: "button",
        text: this.triggerText || "My Device",
        destination: "Design Lab",
        catalog_number: this.catalogNumber,
        composition_name: currentColor,
        source: this.source,
        trigger: this.triggerSource || "upload_section"
      },
      true
    );

    this.showUploadProgress(file.name);
    this.trackUploadStarted(file);
    this.uploadFile(file);
  }

  showUploadProgress(fileName) {
    this.originalContent = this.$dropZone.innerHTML;
    this.$dropZone.innerHTML = `
      <div class="text-center py-4">
        <p class="text-lg font-semibold mb-2">Uploading ${fileName}...</p>
        <div class="w-full bg-gray-200 rounded-full h-2">
          <div class="js-progress-bar bg-blue-600 h-2 rounded-full transition-all duration-300" style="width: 0%"></div>
        </div>
      </div>
    `;
  }

  prepareFormData(file) {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("generate_link", true);
    formData.append("is_made", true);
    formData.append("is_made_flash", false);

    if (this.catalogNumber) {
      formData.append("catalog_number", this.catalogNumber);
    }

    // Use active color from URL to handle dynamic color changes
    const activeColor = getActiveColorName() || this.color;
    if (activeColor) {
      formData.append("ac", activeColor);
    }
    if (this.isSample) {
      formData.append("sample", "true");
    }

    return formData;
  }

  handleUploadSuccess(file, response, uploadStartTime) {
    this.project_id = response.project_id;
    // Preserve the original source (camera, upload, etc.) instead of overwriting
    if (!this.source) {
      this.source = "upload";
    }
    this.trackUploadCompleted(file, response, uploadStartTime);

    // Start with the base redirect URL
    const url = new URL(response.redirect_url, window.location.origin);

    // Add side parameter
    url.searchParams.set("side", this.side);

    // Add source parameter if present
    if (this.source) {
      url.searchParams.set("source", this.source);
    }

    const { quantities, sizeData } = this.getPdpData();

    if (quantities) {
      url.searchParams.set("pdp_quantities", JSON.stringify(quantities));
    }
    if (sizeData) {
      url.searchParams.set("pdp_size_data", JSON.stringify(sizeData));
    }

    redirectTo(url.toString());
  }

  async uploadFile(file) {
    const formData = this.prepareFormData(file);
    const uploadStartTime = performance.now();

    try {
      const response = await api.dtf.uploadArtwork(formData, {
        raw: true,
        headers: { "Content-Type": undefined },
        useAxiosForRequest: true,
        onUploadProgress: ({ progress }) => {
          this.updateProgress(progress * 100);
        }
      });

      if (response.redirect_url) {
        this.handleUploadSuccess(file, response, uploadStartTime);
      }
    } catch (error) {
      this.trackUploadCompletedWithError(file, error, uploadStartTime);

      const errorMessage = error.response?.data?.error || "Upload failed. Please try again.";
      this.showError(errorMessage);
    }
  }

  updateProgress(percentage) {
    const progressBar = this.$dropZone.querySelector(".js-progress-bar");
    if (progressBar) {
      progressBar.style.width = `${percentage}%`;
    }
  }

  showError(message) {
    if (this.originalContent) {
      this.$dropZone.innerHTML = this.originalContent;
    }

    const errorDiv = document.createElement("div");
    errorDiv.className =
      "bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 text-center";
    errorDiv.textContent = message;

    this.$dropZone.insertAdjacentElement("beforebegin", errorDiv);

    setTimeout(() => {
      errorDiv.remove();
    }, 5000);
  }

  resetUploadState() {
    if (this.originalContent) {
      this.$dropZone.innerHTML = this.originalContent;
      // Re-initialize element references after DOM restoration
      this.reinitializeElements();
    }
    if (this.$fileInput) {
      this.$fileInput.value = "";
    }
  }

  reinitializeElements() {
    this.initSelectors();
    this.bindEventListeners();
    this.attachButtonEventListeners();
    this.attachDropZoneEventListeners();

    // Reload design project for continue designing feature
    this.loadDesignProject();
  }

  attachButtonEventListeners() {
    // My Device button
    if (this.$myDeviceBtn) {
      this.$myDeviceBtn.addEventListener("click", this.handleMyDeviceClick);
    }

    // My Design Library button
    if (this.$myLibraryBtn) {
      this.$myLibraryBtn.addEventListener("click", this.handleMyLibraryClick);
    }

    // Canva button
    if (this.$canvaBtn) {
      this.$canvaBtn.addEventListener("click", this.handleCanvaClick);
    }

    // Weaver AI Studio button
    if (this.$weaverAiStudioBtn) {
      this.$weaverAiStudioBtn.addEventListener("click", this.handleWeaverAiStudioClick);
    }

    // Continue designing button
    if (this.$continueDesigningBtn) {
      this.$continueDesigningBtn.addEventListener("click", this.handleContinueDesigningClick);
    }

    // File input
    if (this.$fileInput) {
      this.$fileInput.addEventListener("change", this.handleFileSelect);
    }
  }

  attachDropZoneEventListeners() {
    // Prevent default drag behaviors
    ["dragenter", "dragover", "dragleave", "drop"].forEach(eventName => {
      this.$dropZone.addEventListener(eventName, DesignUploadCTA.preventDefaults, false);
    });

    // Highlight on drag
    this.$dropZone.addEventListener("dragenter", this.handleDragEnter, false);
    this.$dropZone.addEventListener("dragover", DesignUploadCTA.preventDefaults, false);

    ["dragleave", "drop"].forEach(eventName => {
      this.$dropZone.addEventListener(eventName, this.handleDragLeave, false);
    });

    // Handle drop
    this.$dropZone.addEventListener("drop", this.handleDrop, false);
  }

  trackUploadStarted(file) {
    const uploadUuid = crypto.randomUUID();
    // Store uploadUuid per file to prevent race conditions
    this.uploadUuids.set(file.name, uploadUuid);

    const uploadType = this.getUploadType();
    const currentColor = getActiveColorName() || this.color;

    this.trackMixpanelUploadStarted({ file, uploadType, currentColor, uploadUuid });
    this.trackRudderstackUploadStarted({ file, uploadType, currentColor, uploadUuid });
  }

  getUploadType() {
    if (this.triggerSource === "camera") return "camera";
    if (this.triggerSource === "upload_section") return "browse";
    return "drop";
  }

  trackMixpanelUploadStarted({ file, uploadType, currentColor, uploadUuid }) {
    submitDtfEvent("image_upload_started", {
      page: this.location || this.source,
      catalog_number: this.catalogNumber,
      composition_name: currentColor,
      upload_type: uploadType,
      file_name: file.name,
      file_type: file.type,
      file_size: file.size,
      dpi: 300,
      business_line: this.businessLine,
      upload_uuid: uploadUuid
    });
  }

  trackRudderstackUploadStarted({ file, uploadType, currentColor, uploadUuid }) {
    trackRudderstackImageUploadStarted({
      catalog_number: this.catalogNumber,
      dpi: 300,
      file_name: file.name,
      file_type: file.type,
      file_size: file.size,
      composition_name: currentColor,
      location: this.location || this.source,
      business_line: this.businessLine,
      is_app: false,
      multi_artwork: false,
      upload_type: uploadType,
      upload_uuid: uploadUuid
    });
  }

  trackUploadCompleted(file, response, uploadStartTime) {
    const currentColor = getActiveColorName() || this.color;
    // Get the uploadUuid for this specific file to prevent race conditions
    const uploadUuid = this.uploadUuids.get(file.name);

    // Use submitCompletedEvent for standardized image_upload_completed tracking
    submitCompletedEvent({
      file,
      response,
      catalogNumber: this.catalogNumber,
      composition_name: currentColor,
      dpi: 300,
      uploadedOn: this.location || this.source,
      multiArtwork: false,
      businessLine: this.businessLine,
      artworksCount: 1,
      uploadStartTime,
      project_id: this.project_id,
      uploadUuid
    });

    // Clean up the uploadUuid for this file
    this.uploadUuids.delete(file.name);
  }

  trackUploadCompletedWithError(file, error, uploadStartTime) {
    const errorResponse = error?.response?.data || error;
    const handledError = ["backend_file_validation", "backend_processing_error"].includes(
      errorResponse?.error_type
    );

    if (errorResponse.error && !errorResponse.success && handledError) {
      // Track completed event with error information
      const errorMessage = error.response?.data?.error || error.message;
      const mockArtwork = {
        filetype: file.type,
        filesize: file.size,
        errors: [errorMessage]
      };

      const currentColor = getActiveColorName() || this.color;
      // Get the uploadUuid for this specific file to prevent race conditions
      const uploadUuid = this.uploadUuids.get(file.name);

      submitCompletedEvent({
        file,
        artwork: mockArtwork,
        catalogNumber: this.catalogNumber,
        composition_name: currentColor,
        dpi: 300,
        uploadedOn: this.location || this.source,
        multiArtwork: false,
        businessLine: this.businessLine,
        artworksCount: 1,
        uploadStartTime,
        validationErrors: [errorMessage],
        pdpType: this.source,
        uploadUuid
      });

      // Clean up the uploadUuid for this file
      this.uploadUuids.delete(file.name);
    }
  }

  withSampleLimitCheck(handler) {
    return e => {
      // If dynamic data isn't loaded yet and this is a sample, wait for it
      if (this.isSample && !window.document.pdpDynamicData) {
        e.preventDefault();
        e.stopPropagation();

        waitFor(
          "pdpDynamicData",
          () => window.document.pdpDynamicData,
          () => {
            if (shouldShowSampleLimitModal(this.isSample)) {
              showSampleLimitModal();
            } else {
              // Use a no-op synthetic event since preventDefault/stopPropagation
              // were already handled synchronously above
              const noOpEvent = { preventDefault: () => {}, stopPropagation: () => {} };
              handler.call(this, noOpEvent);
            }
          }
        );
        return false;
      }

      if (shouldShowSampleLimitModal(this.isSample)) {
        e.preventDefault();
        e.stopPropagation();
        showSampleLimitModal();
        return false;
      }
      return handler.call(this, e);
    };
  }

  /**
   * Clean up resources and remove event listeners
   * @public
   */
  destroy() {
    // Remove event listeners from buttons
    this.removeButtonEventListeners();

    // Remove event listeners from drop zone
    this.removeDropZoneEventListeners();

    // Clear references
    this.container = null;
    this.$dropZone = null;
    this.$myDeviceBtn = null;
    this.$myLibraryBtn = null;
    this.$weaverAiStudioBtn = null;
    this.$canvaBtn = null;
    this.$fileInput = null;
    this.$continueDesigningCta = null;
    this.$continueDesigningLoader = null;
    this.$continueDesigningBtn = null;
    this.$continueDesigningFront = null;
    this.$continueDesigningBack = null;
  }

  /**
   * Remove button event listeners
   * @private
   */
  removeButtonEventListeners() {
    if (this.$myDeviceBtn) {
      this.$myDeviceBtn.removeEventListener("click", this.handleMyDeviceClick);
    }

    if (this.$myLibraryBtn) {
      this.$myLibraryBtn.removeEventListener("click", this.handleMyLibraryClick);
    }

    if (this.$canvaBtn) {
      this.$canvaBtn.removeEventListener("click", this.handleCanvaClick);
    }

    if (this.$weaverAiStudioBtn) {
      this.$weaverAiStudioBtn.removeEventListener("click", this.handleWeaverAiStudioClick);
    }

    if (this.$continueDesigningBtn) {
      this.$continueDesigningBtn.removeEventListener("click", this.handleContinueDesigningClick);
    }

    if (this.$fileInput) {
      this.$fileInput.removeEventListener("change", this.handleFileSelect);
    }
  }

  /**
   * Remove drop zone event listeners
   * @private
   */
  removeDropZoneEventListeners() {
    if (!this.$dropZone) return;

    // Remove drag and drop event listeners
    ["dragenter", "dragover", "dragleave", "drop"].forEach(eventName => {
      this.$dropZone.removeEventListener(eventName, DesignUploadCTA.preventDefaults, false);
    });

    this.$dropZone.removeEventListener("dragenter", this.handleDragEnter, false);
    this.$dropZone.removeEventListener("dragover", DesignUploadCTA.preventDefaults, false);

    ["dragleave", "drop"].forEach(eventName => {
      this.$dropZone.removeEventListener(eventName, this.handleDragLeave, false);
    });

    this.$dropZone.removeEventListener("drop", this.handleDrop, false);
  }
}

const initDesignUploadModal = () => {
  const $containers = document.querySelectorAll(".js-design-upload-cta");

  // Clean up instances that are no longer in the DOM
  instances.forEach((instance, $container) => {
    if (!document.contains($container)) {
      // Properly destroy the instance before removing it
      try {
        instance.destroy();
      } catch (error) {
        console.error("Error destroying DesignUploadCTA instance:", error);
      }
      instances.delete($container);
    }
  });

  $containers.forEach($container => {
    if (!instances.has($container)) {
      const instance = new DesignUploadCTA($container);
      instance.init();
      instances.set($container, instance);
    }
  });
};

export const resetInitSections = () => {
  window.addEventListener(
    "pageshow",
    e => {
      if (e.persisted) {
        const $sections = document.querySelectorAll(".js-design-upload-cta");
        $sections.forEach($section => {
          const instance = instances.get($section);
          if (instance) {
            instance.resetUploadState();
          }
        });
      }
    },
    false
  );
};

// eslint-disable-next-line max-lines-per-function
export const showDesignUploadModal = (isDynamic = false, modalData = {}) => {
  const $modal = document.getElementById("design-upload-modal");
  const {
    catalogNumber,
    color,
    compositionId,
    designLibraryUrl,
    canvaUrl,
    pdpQuantities,
    pdpSizeData
  } = modalData;

  if (isDynamic) {
    const $designLibraryLink = $modal.querySelector(".js-my-library-btn");
    if ($designLibraryLink && designLibraryUrl) {
      const url = new URL(designLibraryUrl, window.location.origin);
      // Add PDP data to design library URL
      if (pdpQuantities) {
        url.searchParams.set("pdp_quantities", JSON.stringify(pdpQuantities));
      }
      if (pdpSizeData) {
        url.searchParams.set("pdp_size_data", JSON.stringify(pdpSizeData));
      }
      $designLibraryLink.href = url.toString();
    }

    const $canvaLink = $modal.querySelector(".js-canva-btn");
    if ($canvaLink && canvaUrl) {
      const url = new URL(canvaUrl, window.location.origin);
      // Add PDP data to canva URL
      if (pdpQuantities) {
        url.searchParams.set("pdp_quantities", JSON.stringify(pdpQuantities));
      }
      if (pdpSizeData) {
        url.searchParams.set("pdp_size_data", JSON.stringify(pdpSizeData));
      }
      $canvaLink.href = url.toString();
    }

    // Initialize if needed and get the modal's instance
    const modalContainer = $modal.querySelector(".js-design-upload-cta");
    const modalInstance = instances.get(modalContainer);
    if (modalInstance) {
      modalInstance.handleDataUpdate({
        catalogNumber,
        color,
        compositionId
      });
      // Store PDP data in the instance for later use
      modalInstance.pdpQuantities = pdpQuantities;
      modalInstance.pdpSizeData = pdpSizeData;
    }
  }

  if ($modal) {
    const startTime = Date.now();

    $($modal)
      .modal("show")
      .on("hidden.bs.modal", () => {
        const duration = Date.now() - startTime;

        track("upload_step_abandoned", {
          product_id: modalData.productId,
          trigger_location: modalData.isFeatured ? "Top Tile (G640)" : "Product Card",
          abandoned: true,
          time_on_step: `${Math.round(duration / 1000)}s`
        });
      });
  }

  window.addEventListener(
    "pageshow",
    e => {
      if (e.persisted) {
        setTimeout(() => {
          $($modal).modal("hide");
          const modalContainer = $modal.querySelector(".js-design-upload-cta");
          const modalInstance = instances.get(modalContainer);
          if (modalInstance) {
            modalInstance.resetUploadState();
          }
        });
      }
    },
    false
  );
};

export default initDesignUploadModal;
